import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';
import { sealCredential, type SealableCredential, type SealReservation } from '../lib/credentialSeal';

export interface Credential {
  id: string;
  name: string;
  type: 'usernamePassword' | 'sshKey' | 'snmpV2Community' | 'snmpV3' | 'x509Cert' | 'apiKey' | 'preSharedKey';
  description: string | null;
  organizationId: string;
  divisionId: string | null;
  regionId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

/** What a person reads for each credential type. Never show the raw string. */
export const CREDENTIAL_TYPE_LABELS: Record<Credential['type'], string> = {
  usernamePassword: 'Username/Password',
  sshKey: 'SSH Key',
  snmpV2Community: 'SNMPv2 Community',
  snmpV3: 'SNMPv3',
  x509Cert: 'X.509 Certificate',
  apiKey: 'API Key',
  preSharedKey: 'Pre-shared key',
};

export function credentialTypeLabel(type: string): string {
  return (CREDENTIAL_TYPE_LABELS as Record<string, string | undefined>)[type] ?? type;
}

interface CredentialListResponse {
  credentials: Credential[];
}

const backendApi = config.backendApi;

const fetchCredentials = async (): Promise<Credential[]> => {
  const response = await axios.get<CredentialListResponse>(`${backendApi}/api/credentials/`, { withCredentials: true });
  // Defensive: tolerate an unexpected response shape (e.g. API version skew
  // during a rolling deploy) instead of letting a non-array reach `.filter`.
  return Array.isArray(response.data.credentials) ? response.data.credentials : [];
};

export function useCredentials(organizationId: string | null | undefined): UseQueryResult<Credential[]> {
  const contextReady = useActiveContextReady();

  return useQuery<Credential[]>({
    queryKey: ['credentials', organizationId],
    queryFn: () => {
      if (!organizationId) {
        throw new Error("organizationId is required to fetch credentials.");
      }
      return fetchCredentials();
    },
    enabled: !!organizationId && contextReady,
  });
}

interface CreateCredentialCommon {
  name: string;
  regionId: string;
  divisionId?: string;
  description?: string;
}

export type CreateCredentialRequest = CreateCredentialCommon &
  (
    | { type: 'usernamePassword'; username: string; password: string }
    | { type: 'apiKey'; key: string }
    | { type: 'preSharedKey'; key: string }
  );

/** What the reserve step answers: the ids and gateways the browser seals to. */
interface CredentialReservation extends SealReservation {
  version: number;
  expiresAt: string;
}

/** A gateway that didn't take its sealed copy. Replication brings it one later. */
export interface UndeliveredCopy {
  gatewayId: string;
  gatewayName: string;
  reason: string;
}

export interface SealedCreateResponse extends Credential {
  undelivered: UndeliveredCopy[];
}

/** Everything a create carries besides the secret. */
export interface CredentialCreateFields {
  name: string;
  regionId: string;
  divisionId?: string;
  description?: string | null;
}

/**
 * Create a credential without the secret ever reaching our servers
 * (docs/ClientSealedCredentials.md): reserve an id and the gateways to seal to,
 * encrypt the secret once per gateway here, then commit the encrypted copies.
 */
export async function createSealedCredential(
  request: CredentialCreateFields & SealableCredential,
): Promise<SealedCreateResponse> {
  const { data: reservation } = await axios.post<CredentialReservation>(
    `${backendApi}/api/credentials/reservations`,
    {
      name: request.name,
      regionId: request.regionId,
      divisionId: request.divisionId,
      description: request.description,
      type: request.type,
    },
    { withCredentials: true },
  );
  const copies = sealCredential(request, reservation);
  const { data } = await axios.post<SealedCreateResponse>(
    `${backendApi}/api/credentials/`,
    { reservationId: reservation.reservationId, copies },
    { withCredentials: true },
  );
  return data;
}

/** Replace a credential's secret the same way: reserve, seal, commit. */
export async function replaceSealedMaterial(
  id: string,
  credential: SealableCredential,
): Promise<ReplaceCredentialMaterialResponse> {
  const { data: reservation } = await axios.post<CredentialReservation>(
    `${backendApi}/api/credentials/${id}/material/reservations`,
    {},
    { withCredentials: true },
  );
  const copies = sealCredential(credential, reservation);
  const { data } = await axios.post<ReplaceCredentialMaterialResponse>(
    `${backendApi}/api/credentials/${id}/material`,
    { reservationId: reservation.reservationId, copies },
    { withCredentials: true },
  );
  return data;
}

export function useCreateCredential(organizationId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation<Credential, Error, CreateCredentialRequest>({
    mutationFn: (data) => createSealedCredential(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['credentials', organizationId] });
    },
  });
}

/// New material for an existing credential. The material is flattened into the
/// body exactly as it is on create, so both requests speak one shape.
export type ReplaceCredentialMaterialRequest =
  | { id: string; type: 'usernamePassword'; username: string; password: string }
  | { id: string; type: 'sshKey'; key: string }
  | { id: string; type: 'snmpV2Community'; community: string }
  | { id: string; type: 'apiKey'; key: string }
  | { id: string; type: 'preSharedKey'; key: string }
  | {
      id: string;
      type: 'snmpV3';
      username: string;
      authProtocol?: string;
      authKey?: string;
      privProtocol?: string;
      privKey?: string;
    };

/// What republishing a credential's material reported back.
export interface ReplaceCredentialMaterialResponse {
  id: string;
  version: number;
  deployedTo: number;
  pendingOn: number;
  /** Sealed copies a gateway did not take; omitted when there are none. */
  undelivered?: UndeliveredCopy[];
}

/**
 * Republish a credential's material into the same credential id.
 *
 * The backend never stored the secret, so when every gateway holding it is gone
 * the secret is gone with it while the credential — and every device pointing at
 * it — remains. This is the only way back, and the same call rotates material
 * that is merely being replaced.
 *
 * Deliberately not folded into `useCreateCredential`: the id survives, so the
 * caches keyed on it stay valid and only the credential list needs refreshing.
 */
export function useReplaceCredentialMaterial(organizationId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation<ReplaceCredentialMaterialResponse, Error, ReplaceCredentialMaterialRequest>({
    mutationFn: ({ id, ...material }) => replaceSealedMaterial(id, material),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['credentials', organizationId] });
    },
  });
}
